import classNames from "classnames";
import isFunction from "lodash/isFunction";
import isNil from "lodash/isNil";
import uniqueId from "lodash/uniqueId";
import { Label, LabelIcons } from "PFComponents/label";
import { Input, InputProps } from "PFComponents/text/input";
import { TextArea, TextAreaProps } from "PFComponents/text/text_area";
import { getAriaProps } from "PFCore/helpers/get_aria_props";
import { IconName } from "PFTheme/graphics/icons";
import {
  AriaAttributes,
  CSSProperties,
  ForwardedRef,
  forwardRef,
  HTMLInputTypeAttribute,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useToggle } from "react-use";

import { Icon } from "../../icon";
import { Typography } from "../../typography";
import { InputFieldIcons, InputFieldIconsList } from "./components/input_field_icons";
import { LockedIcon } from "./components/locked_icon";
import { PasswordToggle } from "./components/password_toggle";
import { Tip } from "./components/tip";
import css from "./input_field_set.module.scss";

const INPUT_RIGHT_PADDING = 8;

export type InputFieldSetProps = (
  | ({ inputType: "textarea" } & Omit<TextAreaProps, "ref">)
  | ({ inputType?: HTMLInputTypeAttribute } & Omit<InputProps, "type" | "ref">)
) & {
  label?: string;
  labelIcons?: LabelIcons;
  qaId?: string;

  active?: boolean;
  required?: boolean;

  inline?: boolean;
  locked?: boolean;
  lockedTip?: string;
  lockedTipPosition?: "top" | "bottom";

  tip?: string;
  error?: ReactNode | boolean | null;
  warning?: ReactNode | boolean | null;

  maxLength?: number;
  minVisibleMaxLengthFactor?: number;
  showPasswordToggle?: boolean;

  className?: string;
  inputClassName?: string;

  style?: CSSProperties;
  /* Property for internal usage to style the custom Select values. Not intended for external usage */
  inputStyle?: CSSProperties;

  icon?: IconName;
  actions?: InputFieldIconsList;
  dataInputName?: string;
  variant?: "default" | "table";
} & AriaAttributes;

export const InputFieldSet = forwardRef(
  (
    {
      qaId,
      id,
      title,
      placeholder,
      active,
      disabled,
      required,
      className,
      inputClassName,
      style,
      inputStyle,
      inputType,
      label,
      labelIcons,
      inline,
      locked,
      lockedTip,
      lockedTipPosition = "top",
      maxLength,
      minVisibleMaxLengthFactor = -1,
      name,
      onBlur,
      onFocus,
      onKeyDown,
      readOnly,
      showPasswordToggle,
      value,
      tip,
      error,
      warning,
      dataInputName,
      icon,
      actions,
      variant = "default",
      ...rest
    }: InputFieldSetProps,
    ref: ForwardedRef<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { current: tagId } = useRef(id || uniqueId(name));
    const [showPassword, handlePasswordToggle] = useToggle(false);
    const [isFocused, setIsFocused] = useState(false);
    const valueLength = useMemo(() => (value ? String(value).length : 0), [value]);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const [internalIconsRef, setInternalIconsRef] = useState<HTMLLabelElement | HTMLDivElement | null>(null);

    const { ariaProps, rest: inputProps } = getAriaProps(rest);

    const handleFocus = useCallback(
      (...args) => {
        setIsFocused(true);
        //@ts-ignore-next-line
        onFocus?.(...args);
      },
      [onFocus]
    );

    const handleBlur = useCallback(
      (...args) => {
        setIsFocused(false);
        //@ts-ignore-next-line
        onBlur?.(...args);
      },
      [onBlur]
    );

    const calcInputType = inputType === "password" && showPassword ? "text" : inputType;

    const Tag = inputType === "textarea" ? TextArea : Input;
    const showMaxLength = maxLength && valueLength > minVisibleMaxLengthFactor * maxLength;

    const shouldRenderFooter = (error || warning || tip || showMaxLength) && variant !== "table";

    const rootCss = classNames(
      css.root,
      {
        [css.readOnly]: readOnly,
        [css.focused]: isFocused,
        [css.inline]: inline,

        [css.active]: inputRef.current?.value || active || isFocused || !!valueLength || !!placeholder,
        [css.locked]: locked,
        [css.tableVariant]: variant === "table"
      },
      className
    );

    const inputContainerCss = classNames(css.wrap, { [css.tableVariant]: variant === "table" });

    const inputCss = classNames(css.input, inputClassName, {
      [css.disabled]: disabled || locked,
      [css.error]: error,
      [css.warning]: warning && !error,
      [css.tableVariant]: variant === "table"
    });

    useEffect(() => {
      if (isFunction(ref)) {
        ref(inputRef.current);
      } else if (ref) {
        ref.current = inputRef.current;
      }
    }, []);

    const finalInputStyle = {
      ...inputStyle,
      paddingRight: (internalIconsRef?.offsetWidth || 0) + INPUT_RIGHT_PADDING
    };

    const InputIconsWrapper = disabled ? "div" : "label";

    return (
      <div className={rootCss} data-qa-id={qaId} style={style}>
        {label && (
          <div className={css.labelRow}>
            <Label
              text={label}
              icons={labelIcons}
              required={required}
              inputId={tagId}
              disabled={disabled}
              className={css.label}
            />
            <InputFieldIcons icons={actions || []} disabled={locked} />
          </div>
        )}
        <div className={inputContainerCss}>
          <Tag
            {...(variant === "table" && inputType === "textarea"
              ? { maxHeight: "100%", height: "100%" }
              : {})}
            {...inputProps}
            style={finalInputStyle}
            aria-required={required}
            className={inputCss}
            // @ts-ignore ts cannot predict if Tag is TextArea or Input
            ref={inputRef}
            id={tagId}
            value={!isNil(value) ? String(value) : value}
            readOnly={readOnly}
            placeholder={placeholder}
            disabled={locked || disabled}
            title={title || label || ariaProps["aria-label"]}
            aria-disabled={locked || disabled}
            type={calcInputType}
            onFocus={handleFocus}
            onBlur={handleBlur}
            //@ts-ignore-next-line
            onKeyDown={onKeyDown}
            maxLength={maxLength}
            data-input-name={dataInputName}
            aria-activedescendant={ariaProps["aria-activedescendant"]}
            aria-label={ariaProps["aria-label"]}
            aria-invalid={error ? "true" : undefined}
            aria-expanded={ariaProps["aria-expanded"]}
            aria-controls={ariaProps["aria-controls"]}
          />
          <InputIconsWrapper htmlFor={tagId} ref={setInternalIconsRef as any} className={css.internalIcons}>
            {showPasswordToggle && inputType === "password" && (
              <PasswordToggle showPassword={showPassword} onClick={handlePasswordToggle} />
            )}
            {!label && (
              <InputFieldIcons icons={actions || []} disabled={locked} tabIndex={inputProps.tabIndex} />
            )}
            {!locked && icon && <Icon size="sm" name={icon} className={css.icon} color="paletteBlue0" />}
            {locked && <LockedIcon lockedTip={lockedTip} lockedTipPosition={lockedTipPosition} />}
          </InputIconsWrapper>
        </div>
        {shouldRenderFooter && variant === "default" && (
          <div className={css.footer}>
            {(error || warning || tip) && <Tip error={error} warning={warning} tip={tip} />}
            {showMaxLength && (
              <Typography
                withMargin
                variant="labelRegular"
                className={css.maxLength}
              >{`${valueLength} / ${maxLength}`}</Typography>
            )}
          </div>
        )}
      </div>
    );
  }
);

InputFieldSet.displayName = "InputFieldSet";
